/**
 * Where to send someone after they authenticate.
 */
import { PORTALS } from '@/app/portals'
import type { Me } from '@/shared/api/types'

import { isRole } from './roles'

/**
 * True while a deliberate sign-out is in flight.
 *
 * ⚠️ **Signing out is not the same event as arriving at a guarded page
 * logged-out, and conflating them is a bug users hit every time.** Clearing the
 * session re-renders whatever screen they were on; if that screen sits behind
 * `RequireAuth`, the guard fires *before* the sign-out handler's own
 * `navigate('/')` lands, and it helpfully captures where they were —
 * `/login?next=%2Fme%2Fnotifications`. The next person to sign in on that tab
 * is then dropped on the previous user's page.
 *
 * `next` exists for the opposite case: someone followed a deep link, got asked
 * to sign in, and should be returned to what they wanted. That is worth
 * keeping, so the fix is to tell the guard which event this is rather than to
 * delete the feature.
 *
 * A module-level flag rather than state: the guard reads it during the same
 * synchronous render pass that the session clears in, and React state would
 * arrive a render too late.
 */
let signingOut = false

export function beginSignOut(): void {
  signingOut = true
}

export function endSignOut(): void {
  signingOut = false
}

export function isSigningOut(): boolean {
  return signingOut
}

/**
 * A `?next=` value that is safe to navigate to.
 *
 * Only a path on this origin. `//evil.example` and `https://evil.example` are
 * both valid values of `next` as far as the URL parser is concerned, and both
 * would turn our login page into an open redirect — a phishing flow that starts
 * on the real site.
 */
export function safeNext(next: string | null): string | null {
  if (!next) return null
  if (!next.startsWith('/')) return null
  if (next.startsWith('//')) return null
  return next
}

/**
 * Whether the given account may actually land on `path`.
 *
 * **UX guard, not a security boundary** — exactly like `RequireRole` itself
 * (see that file's docstring). This only decides where to *redirect*; the
 * server's own 403 is what actually stops a disallowed request. Nothing here
 * should ever be read as the authorisation check.
 *
 * `PORTALS` (`app/portals.tsx`) is the single source of truth for which roles
 * a portal prefix requires — the same list `PortalRoute` guards routes with —
 * so this reads it rather than keeping a second map that could drift. A path
 * that matches no portal (`/me/listings`, `/courses/:id`, `/checkout/:id`,
 * …) is either public or merely `RequireAuth`-gated: any signed-in account may
 * go there, so it is permitted by default.
 */
export function mayEnter(me: Me, path: string): boolean {
  const pathname = path.split(/[?#]/)[0] ?? path

  const portal = PORTALS.find((candidate) => {
    // `path` on `PortalDefinition` is either the exact root ('/') or a
    // wildcard subtree ('/teach/*', '/organize/*', …) — mirror the same
    // prefix match `router.tsx` uses to mount them.
    if (candidate.path === '/') return pathname === '/'
    const prefix = candidate.path.replace(/\/\*$/, '')
    return pathname === prefix || pathname.startsWith(`${prefix}/`)
  })

  if (!portal) return true
  if (portal.allowAnonymous) return true

  const roles = (me.roles ?? []).filter(isRole)
  return portal.roles.some((role) => roles.includes(role))
}

/**
 * The landing route after a successful sign-in.
 *
 * Onboarding wins over `next`, but carries it: someone who was heading for a
 * course still gets there once they are through, rather than being dropped on
 * the home page having forgotten why they came.
 *
 * `next` is only ever honoured when the freshly-signed-in account can actually
 * use it (`mayEnter`) — otherwise it falls back to the home page. Without that
 * check, whoever signs in next on a shared browser tab inherits whatever
 * page the previous, differently-privileged person was headed for.
 */
export function postLoginPath(me: Me, next: string | null): string {
  const wanted = safeNext(next)
  const target = wanted && mayEnter(me, wanted) ? wanted : null

  // Optional chaining despite the schema typing `profile` non-nullable: a
  // user created outside registration has no `Profile` row, and this line
  // runs on the login response — before anything could create one. The
  // backend now always sends an object, and this is the belt to that
  // braces, because the failure mode is a blank page rather than a
  // degraded one.
  if (me.profile?.onboarding_completed_at == null) {
    return target ? `/onboarding?next=${encodeURIComponent(target)}` : '/onboarding'
  }

  return target ?? '/'
}

/**
 * `/login?next=…`, so logging in returns the user to where they aimed.
 *
 * ⚠️ **Except during a sign-out**, when there is no "where they aimed" — they
 * aimed at leaving. Capturing the page they were signing out *of* strands the
 * next person to use the tab on someone else's screen, which is what
 * `isSigningOut()` prevents. See `navigation.ts`.
 */
export function loginPath(pathname: string, search: string): string {
  if (isSigningOut()) return '/'
  const target = `${pathname}${search}`
  return target === '/' ? '/login' : `/login?next=${encodeURIComponent(target)}`
}
